import React, { useMemo, useState, useCallback } from 'react';
import { matchPath } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectCurrentGroupSizeFromLoaded,
  selectCurrentGroupTitleFromLoaded,
  selectManageGroupBy,
  selectVulnDetailSelectedTabId,
  selectVulnDetail,
  selectVulnsList
} from 'store/Manage/selectors';
import {
  hideVulnerabilityDetail,
  loadVulnerabilityDetail,
  setVulnerabilityDetailTab,
  showBulkUpdateModal
} from 'store/Manage/actions';
import { selectPathname } from 'store/Router/selectors';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { FEATURE_VULNS, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';
import { selectCurrentWorkspace, selectIsReadOnly, selectWorkspaces } from 'store/Faraday/selectors';
import { useIntl } from 'react-intl';
import { exploreGroup, renameManualGroup as renameManualGroupAction } from 'store/GroupVulns/actions';

import CopyLink from './components/CopyLink';
import CopyApiLink from './components/CopyApiLink';
import GroupingBadge from './components/GroupingBadge';
import EditGroupNameModal from './components/EditGroupNameModal';

import {
  Wrapper,
  InputTitle,
  IconVulnerability,
  EmptyFolderIcon,
  GoToButton,
  GoToIcon,
  LeftCluster,
  RightActions
} from './styled';

const isDefined = (v) => v !== null && typeof v !== 'undefined';
const asTrimmedStringOrNull = (v) => {
  const s = (v ?? '').toString().trim();
  return s.length ? s : null;
};
const toVulnValue = (v) => v?.value ?? v ?? {};
const normalizeCves = (cves) => {
  if (!Array.isArray(cves)) return [];
  return cves
    .map((cve) => String(cve ?? '').trim().toUpperCase())
    .filter(Boolean);
};
const inferAutomaticGroupCriterion = (groupHeaderId, vulnsList = []) => {
  if (groupHeaderId === null || typeof groupHeaderId === 'undefined') return 'name';

  const members = (Array.isArray(vulnsList) ? vulnsList : [])
    .map(toVulnValue)
    .filter((item) => item?.is_automatic === true)
    .filter((item) => {
      const itemId = item?._id ?? item?.id ?? null;
      const itemGroupKey = item?.group_id ?? item?.group_vuln_id ?? (item?.is_main === true ? itemId : null);
      return String(itemGroupKey) === String(groupHeaderId);
    });

  if (members.length < 2) return 'name';

  const cveSets = members.map((item) => new Set(normalizeCves(item?.cve)));
  const allHaveCves = cveSets.every((set) => set.size > 0);
  if (!allHaveCves) return 'name';

  const [firstSet, ...restSets] = cveSets;
  const hasCommonCve = Array.from(firstSet).some((cve) => restSets.every((set) => set.has(cve)));
  return hasCommonCve ? 'cve' : 'name';
};

const getExistingGroupNames = (vulns = []) => {
  const names = (Array.isArray(vulns) ? vulns : [])
    .map(toVulnValue)
    .map((item) => String(item?.group_title ?? item?.name ?? '').trim())
    .filter(Boolean);

  return Array.from(new Set(names));
};

const Title = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const vulnDetail = useSelector(selectVulnDetail);
  const vulnDetailSelectedTab = useSelector(selectVulnDetailSelectedTabId);
  const vulnsList = useSelector(selectVulnsList);
  const pathname = useSelector(selectPathname);

  const readOnly = useSelector(selectIsReadOnly);
  const canUpdate = useSelector((state) =>
    selectAllowedByRole(state, FEATURE_VULNS, PERMISSION_TYPES.UPDATE)
  );

  const groupBy = useSelector(selectManageGroupBy);
  const currentWorkspaceName = useSelector(selectCurrentWorkspace);
  const workspaces = useSelector(selectWorkspaces);
  const groupTitleFromLoaded = useSelector(selectCurrentGroupTitleFromLoaded);
  const groupSizeFromLoaded = useSelector(selectCurrentGroupSizeFromLoaded);
  const vulnIsAutomatic = vulnDetail?.is_automatic;
  const vulnIsMain = vulnDetail?.is_main;
  const vulnGroupId = vulnDetail?.group_id;
  const vulnGroupVulnId = vulnDetail?.group_vuln_id;
  const vulnId = vulnDetail?._id;
  const vulnGroupTitle = vulnDetail?.group_title;
  const vulnGroupBy = vulnDetail?.group_by;
  const vulnDetailName = vulnDetail?.name;
  const vulnGroupCount = vulnDetail?.group_count;
  const isExploreRoute = Boolean(matchPath(pathname, {
    path: '/vulns/:ws/groups/:groupId',
    exact: true,
    strict: false
  }));

  const isGrouped = useMemo(() => {
    return isDefined(vulnIsAutomatic);
  }, [vulnIsAutomatic]);

  const isGroupHeader = useMemo(() => {
    if (!isGrouped) return false;
    return vulnIsMain === true || !isDefined(vulnGroupVulnId);
  }, [isGrouped, vulnIsMain, vulnGroupVulnId]);

  const groupHeaderId = useMemo(() => {
    if (!isGrouped) return vulnId ?? null;
    return isDefined(vulnGroupVulnId) ? vulnGroupVulnId : vulnId ?? null;
  }, [isGrouped, vulnId, vulnGroupVulnId]);
  const exploreGroupId = useMemo(() => {
    if (!isGrouped) return vulnId ?? null;
    return isDefined(vulnGroupId) ? vulnGroupId : groupHeaderId;
  }, [isGrouped, vulnId, vulnGroupId, groupHeaderId]);
  const renameGroupId = useMemo(() => {
    if (!isGrouped) return vulnId ?? null;
    return isDefined(vulnGroupId) ? vulnGroupId : groupHeaderId;
  }, [isGrouped, vulnId, vulnGroupId, groupHeaderId]);

  const resolvedGroupTitle = useMemo(() => {
    return (
      asTrimmedStringOrNull(vulnGroupTitle) ??
      asTrimmedStringOrNull(groupTitleFromLoaded) ??
      asTrimmedStringOrNull(vulnDetailName) ??
      ''
    );
  }, [vulnGroupTitle, groupTitleFromLoaded, vulnDetailName]);

  const resolvedGroupCount = useMemo(() => {
    if (!isGrouped) return null;

    const beCount = vulnGroupCount;
    if (typeof beCount === 'number') {
      return beCount;
    }

    if (typeof groupSizeFromLoaded === 'number') {
      return groupSizeFromLoaded;
    }

    return null;
  }, [isGrouped, vulnGroupCount, groupSizeFromLoaded]);

  const displayTitle = useMemo(() => {
    if (isExploreRoute) return vulnDetailName ?? '';
    if (isGrouped) return vulnDetailName ?? '';

    const base = resolvedGroupTitle ?? vulnDetailName ?? '';
    return resolvedGroupCount ? `${base} (${resolvedGroupCount})` : base;
  }, [isExploreRoute, isGrouped, vulnDetailName, resolvedGroupTitle, resolvedGroupCount]);

  const titleTooltip = useMemo(
    () => intl.formatMessage({ id: 'manage.cm.edit.name' }),
    [intl]
  );

  const canEditName = useMemo(() => {
    return !readOnly && canUpdate && (!isGrouped || isGroupHeader);
  }, [readOnly, canUpdate, isGrouped, isGroupHeader]);

  const [showEditGroupNameModal, setShowEditGroupNameModal] = useState(false);
  const handleOpenNameModal = useCallback(() => {
    if (!canEditName) return;
    dispatch(showBulkUpdateModal('name', vulnDetailName || ''));
  }, [canEditName, dispatch, vulnDetailName]);

  const showExploreGroup = !isExploreRoute && isGrouped && !!exploreGroupId;
  const workspaceGroupBy = useMemo(() => {
    const currentWorkspace = (Array.isArray(workspaces) ? workspaces : [])
      .find((workspace) => workspace?.name === currentWorkspaceName);
    return asTrimmedStringOrNull(currentWorkspace?.group_by);
  }, [workspaces, currentWorkspaceName]);

  const inferredAutomaticGroupCriterion = useMemo(() => {
    if (vulnIsAutomatic !== true) return '';
    return inferAutomaticGroupCriterion(exploreGroupId, vulnsList);
  }, [vulnIsAutomatic, exploreGroupId, vulnsList]);

  const resolvedAutomaticGroupCriterion = useMemo(() => {
    if (vulnIsAutomatic !== true) return '';
    return (
      asTrimmedStringOrNull(vulnGroupBy) ??
      workspaceGroupBy ??
      asTrimmedStringOrNull(groupBy) ??
      inferredAutomaticGroupCriterion
    );
  }, [vulnIsAutomatic, vulnGroupBy, workspaceGroupBy, groupBy, inferredAutomaticGroupCriterion]);

  const handleExploreClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (!exploreGroupId) return;

      dispatch(
        exploreGroup({
          entity: 'vulns',
          groupId: exploreGroupId,
          groupHeaderId,
          groupDisplayName: vulnDetailName || '',
          groupBadgeTitle: resolvedGroupTitle || '',
          groupByCriterion: resolvedAutomaticGroupCriterion,
          groupVulnsCount: resolvedGroupCount,
          isAutomaticGroup: vulnIsAutomatic === true,
          source: 'vuln_detail_title',
          mode: 'explore'
        })
      );
    },
    [dispatch, exploreGroupId, groupHeaderId, resolvedGroupTitle, vulnDetailName, resolvedAutomaticGroupCriterion, resolvedGroupCount, vulnIsAutomatic]
  );

  const badgeVuln = useMemo(
    () => ({
      ...(vulnDetail ?? {}),
      group_title: resolvedGroupTitle,
      group_by: (
        asTrimmedStringOrNull(vulnGroupBy) ??
        workspaceGroupBy ??
        asTrimmedStringOrNull(groupBy) ??
        inferredAutomaticGroupCriterion
      )
    }),
    [vulnDetail, resolvedGroupTitle, vulnGroupBy, workspaceGroupBy, groupBy, inferredAutomaticGroupCriterion]
  );
  const existingGroupNames = useMemo(() => getExistingGroupNames(vulnsList), [vulnsList]);
  const canRenameManualGroup = useMemo(() => {
    return Boolean(renameGroupId) && vulnIsAutomatic === false;
  }, [renameGroupId, vulnIsAutomatic]);
  const handleOpenEditGroupNameModal = useCallback(() => {
    if (!canRenameManualGroup) return;
    setShowEditGroupNameModal(true);
  }, [canRenameManualGroup]);
  const handleCloseEditGroupNameModal = useCallback(() => setShowEditGroupNameModal(false), []);
  const handleSaveEditGroupName = useCallback(
    async (newGroupName) => {
      if (!canRenameManualGroup || !renameGroupId) return false;

      const res = await dispatch(renameManualGroupAction('vulns', renameGroupId, newGroupName));
      if (!res) return false;

      if (vulnId) {
        dispatch(hideVulnerabilityDetail());
        if (vulnDetailSelectedTab) {
          dispatch(setVulnerabilityDetailTab(vulnDetailSelectedTab));
        }
        await dispatch(loadVulnerabilityDetail(vulnId, true));
      }

      return true;
    },
    [canRenameManualGroup, dispatch, renameGroupId, vulnId, vulnDetailSelectedTab]
  );

  return (
    <Wrapper>
      <EditGroupNameModal
        show={ showEditGroupNameModal }
        currentGroupName={ resolvedGroupTitle }
        existingGroupNames={ existingGroupNames }
        onClose={ handleCloseEditGroupNameModal }
        onSave={ handleSaveEditGroupName }
      />
      <LeftCluster>
        {isGroupHeader && !isExploreRoute ? <EmptyFolderIcon /> : <IconVulnerability />}

        <InputTitle
          title={ titleTooltip }
          value={ displayTitle }
          onClick={ handleOpenNameModal }
          disabled={ !canEditName }
          readOnly
          size={ displayTitle.length || 1 }
        />

        {isGrouped && !isExploreRoute && (
          <GroupingBadge
            vuln={ badgeVuln }
            onClick={ handleOpenEditGroupNameModal }
          />
        )}

        {showExploreGroup && (
          <GoToButton onClick={ handleExploreClick } title="Explore Group">
            <GoToIcon />
          </GoToButton>
        )}
      </LeftCluster>

      <RightActions>
        <CopyLink />
        <CopyApiLink />
      </RightActions>
    </Wrapper>
  );
};

export default Title;
